import React, { memo, useContext, useState } from 'react';

import { useExperiment } from '@olx/react-laquesis';
import { Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import { useTrackEvent } from '@olxeu-a2b/delivery-utils/src/tracking/useNinja';
import trackingKeys from '../../../../commons/constants/trackingKeys';
import CopyIcon from '../../../../commons/assets/copy.svg';
import { useI18n } from '../../../../i18n';
import StatusReason from './StatusReason';
import TransactionContext from '../contexts/TransactionContext';
import AdImage from './AdImage';
import AdTitle from './AdTitle';
import Status from './Status';
import StatusInfo from './StatusInfo';
import useGetUserType from '../../hooks/useGetUserType';
import DesktopTransactionInfo from './DesktopTransactionInfo';

import S from './style/AdBlock';

interface OrderCardResponsiveProps {
  active: boolean;
  onTitleClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  packageDeliveryInfoUrl: string;
  packageDeliveryBuyerManualUrl: string;
  packageDeliverySellerManualUrl: string;
  showOrderSafeDealPackageDeliveredDescr: boolean;
  goToReturns?: () => void;
}

const AdBlock: React.FC<OrderCardResponsiveProps> = ({
  active,
  onTitleClick,
  packageDeliveryInfoUrl,
  packageDeliveryBuyerManualUrl,
  packageDeliverySellerManualUrl,
  showOrderSafeDealPackageDeliveredDescr,
  goToReturns,
}) => {
  // TODO: Remove when experiment is done
  const isDOPeTweaks = useExperiment('edu2r-6014') === 'b';
  const transaction = useContext(TransactionContext);
  const trackEvent = useTrackEvent();
  const i18n = useI18n();
  const { status = '', ad } = transaction || {};

  const { isSeller } = useGetUserType({
    buyerId: transaction?.buyer?.userId,
    sellerId: transaction?.seller?.userId,
  });

  const isPostingMethodExternalApp =
    (transaction?.shipment as any)?.postingMethod === 'EXTERNAL_APP' &&
    transaction.status === 'SHIPMENT_LABEL_RECEIVED' &&
    isSeller;

  const orderDate =
    transaction?.order?.createdAt &&
    new Date(transaction?.order?.createdAt).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  const [isOpen, setIsOpen] = useState(false);
  const handleOnclick = () => {
    navigator.clipboard.writeText(transaction?.order?.id);
    trackEvent(trackingKeys.events.copyOrderIdClick);
  };

  return (
    <>
      <S.Block>
        <AdImage src={ad?.image} />
        <AdTitle active={active} onTitleClick={onTitleClick} isSeller={isSeller} />
      </S.Block>
      <Status status={status} transaction={transaction} />
      <StatusReason statusReason={transaction?.statusReason} />
      <StatusInfo
        status={status}
        packageDeliveryInfoUrl={packageDeliveryInfoUrl}
        packageDeliveryBuyerManualUrl={packageDeliveryBuyerManualUrl}
        packageDeliverySellerManualUrl={packageDeliverySellerManualUrl}
        showOrderSafeDealPackageDeliveredDescr={showOrderSafeDealPackageDeliveredDescr}
        isPostingMethodExternalApp={isPostingMethodExternalApp}
        goToReturns={goToReturns}
        confirmationExpirationDate={transaction?.order?.confirmationExpirationDate}
        packagePostingExpirationDate={transaction?.shipment?.packagePostingExpirationDate}
        deliveryType={transaction?.deliveryType}
      />
      <S.OrderContent>
        <S.OrderInfoWrapper>
          <S.OrderDateWrapper>
            {orderDate && isDOPeTweaks ? (
              <Paragraph4 lineHeight={14} fontSize={12} color="secondary">
                {i18n(`dlv_order_card_desktop_order_date`)} {orderDate}
              </Paragraph4>
            ) : null}
          </S.OrderDateWrapper>
          <S.OrderIdWrapper>
            {transaction?.order?.shortId && isDOPeTweaks ? (
              <>
                <Paragraph4 lineHeight={14} fontSize={12} color="secondary">
                  {i18n(`dlv_order_card_desktop_order_id`)} {transaction?.order?.shortId}
                </Paragraph4>
                <S.TooltipWrapper
                  visible={isOpen}
                  position="topRight"
                  onClickOutside={() => setIsOpen(false)}
                  onOpen={() => {
                    setIsOpen(true);
                    handleOnclick();
                  }}
                  toggleNode={<CopyIcon />}
                >
                  {i18n(`dlv_order_card_desktop_order_id_copied`)}
                </S.TooltipWrapper>
              </>
            ) : null}
          </S.OrderIdWrapper>
        </S.OrderInfoWrapper>
        {!active && (
          <DesktopTransactionInfo
            deliveryType={transaction?.deliveryType}
            deliveryProvider={transaction?.deliveryProductId}
          />
        )}
      </S.OrderContent>
    </>
  );
};

export default memo(AdBlock);
